/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useMemo } from 'react';
import * as Ron from 'blb-ui-types/ron';
import compact from 'lodash/compact';
import { Box, Text } from 'grommet';
import { convertToDateFormat } from 'blb-utils/date';
import { DirectionType, MarginType } from 'grommet/utils';
import { CredentialAttrKeys, CredentialAttrMeta } from 'blb-constants/ron';

export namespace PageSectionHeader {
  export interface Props {
    credential: Ron.API.UserCredential;
    sameRowKeyValueLayout?: boolean;
  }
}

export const CredentialAttributes: React.FC<PageSectionHeader.Props> = ({
  credential,
  sameRowKeyValueLayout = false,
}) => {
  const attrKeys = CredentialAttrKeys[credential.type];
  const fields: [string, string][] = useMemo(
    () =>
      compact(
        attrKeys.map(attrKey => {
          const label: string = CredentialAttrMeta[attrKey].label;
          const value: number | string | null | undefined = (credential.meta as any)[attrKey];
          let valueTxt: string;

          if (typeof value === 'number') {
            valueTxt = value.toString();
          } else if (!value) {
            valueTxt = '';
          } else {
            valueTxt = value;
          }
          if (attrKey === 'expirationDate') {
            valueTxt = convertToDateFormat(valueTxt);
          }
          return [label, valueTxt];
        }),
      ),
    [credential],
  );

  const renderAttribute = (label: string, value: string) => {
    const {
      direction,
      margin,
    }: {
      direction: DirectionType;
      margin: MarginType;
    } = sameRowKeyValueLayout
      ? {
          direction: 'row',
          margin: 'xsmall',
        }
      : {
          direction: 'column',
          margin: 'small',
        };

    return (
      <Box direction={direction} gap="xsmall" id="it-credentialAttr-item" key={label} margin={{ bottom: margin }}>
        <Text size={sameRowKeyValueLayout ? 'medium' : 'small'}>{label}:</Text>
        <Text color="night90" size="medium">
          {value}
        </Text>
      </Box>
    );
  };
  return <Box id="it-credential-attributes">{fields.map(([label, value]) => renderAttribute(label, value))}</Box>;
};
